import React, { useState, useRef, useEffect } from 'react';
import { subYears } from 'date-fns';
import { Box, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import ReplayIcon from '@mui/icons-material/Replay';
import { LoadingButton } from '@mui/lab';
import { format } from 'date-fns';
import axios from 'axios';
import api from 'appConfig/restAPIs';
import ADDate from 'shared/components/ADDate';
import { DATA_DATE_FORMAT } from 'data/constants';
import { formatUIDate, precisionRound } from 'shared/utils';
import { AgGridReact } from 'ag-grid-react';
import StatementPDF from './StatementPDF';

const IconLoadingButton = styled(LoadingButton)({
  "& .MuiButton-startIcon": {
    marginLeft: 0, marginRight: 0,
  }
});

const Statement = ({ companyId, companyName, role }) => {
  const gridRef = useRef();
  const [statementList, setStatementList] = useState([]);
  const [sum, setSum] = useState({ cost: 0, taxAmount: 0, total: 0, paidAmount: 0, paidTax: 0, paidTotal: 0, balance: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [stDate, setStDate] = useState(() => subYears(new Date(), 1));
  const [edDate, setEdDate] = useState(new Date());

  const columnDefs = [
    { field: "sDate", headerName: "Date", width: 80, valueFormatter: (params) => formatUIDate(params.value) },
    { field: 'adId', headerName: 'Ad Id', width: 80, valueFormatter: (params) => params.value || '' },
    { field: 'adTitle', headerName: 'Title', width: 140 },
    { field: 'adType', headerName: 'Type', width: 80 },
    { field: 'page', headerName: 'Page', width: 80 },
    { field: 'size', headerName: 'Size', width: 80 },
    {
      field: 'cost', headerName: 'Cost', width: 80, resizable: false,
      valueFormatter: (params) => params.value ? precisionRound(params.value).toLocaleString() : ''
    },
    {
      field: 'taxAmount', headerName: 'Tax', width: 80, resizable: false,
      valueFormatter: (params) => params.value ? precisionRound(params.value).toLocaleString() : ''
    },
    {
      field: 'total', headerName: 'Total', width: 80, resizable: false,
      valueGetter: (params) => {
        const val = (params.data.cost || 0) + (params.data.taxAmount || 0);
        return val !== 0 ? val : null;
      },
      valueFormatter: (params) => params.value ? precisionRound(params.value).toLocaleString() : ''
    },
    { field: 'payId', headerName: 'Pay Id', width: 80, valueFormatter: (params) => params.value || '' },
    {
      field: 'paidAmount', headerName: 'Paid', width: 80, resizable: false,
      valueFormatter: (params) => params.value ? precisionRound(params.value).toLocaleString() : ''
    },
    {
      field: 'paidTax', headerName: 'Tax', width: 80, resizable: false,
      valueFormatter: (params) => params.value ? precisionRound(params.value).toLocaleString() : ''
    },
    {
      field: 'paidTotal', headerName: 'Total', width: 80, resizable: false,
      valueGetter: (params) => {
        const val = (params.data.paidAmount || 0) + (params.data.paidTax || 0);
        return val !== 0 ? val : null;
      },
      valueFormatter: (params) => params.value ? precisionRound(params.value).toLocaleString() : ''
    },
    {
      field: 'balance',
      headerName: 'Balance',
      width: 100,
      resizable: false,
      valueFormatter: (params) => precisionRound(params.value).toLocaleString()
    },
    { field: 'method', headerName: 'Method', width: 100 },
  ];


  useEffect(() => {
    if (companyId) {
      setIsLoading(true);
      const fromDate = stDate ? format(stDate, DATA_DATE_FORMAT) : "";
      const toDate = edDate ? format(edDate, DATA_DATE_FORMAT) : "";

      axios.get(`${api.statement}?companyId=${companyId}&startDate=${fromDate}&endDate=${toDate}`)
        .then(resp => {
          let preBalance = 0;
          const newData = resp.data
            .sort((a, b) => new Date(a.sDate) - new Date(b.sDate))
            .map(item => {
              const total = (item.cost || 0) + (item.taxAmount || 0);
              const paidTotal = (item.paidAmount || 0) + (item.paidTax || 0);
              const balance = total - paidTotal + preBalance;
              preBalance = balance;

              return {
                ...item,
                total: total !== 0 ? total : null,
                paidTotal: paidTotal !== 0 ? paidTotal : null,
                balance: balance,
                cost: item.cost !== 0 ? item.cost : null,
                taxAmount: item.taxAmount !== 0 ? item.taxAmount : null,
                paidAmount: item.paidAmount !== 0 ? item.paidAmount : null,
                paidTax: item.paidTax !== 0 ? item.paidTax : null,
                adId: item.adId !== 0 ? item.adId : null,
                payId: item.payId !== 0 ? item.payId : null,
              };
            });

          setStatementList(newData);

          const totalSum = newData.reduce((acc, curr) => {
            acc.cost += curr.cost || 0;
            acc.taxAmount += curr.taxAmount || 0;
            acc.total += (curr.cost || 0) + (curr.taxAmount || 0);
            acc.paidAmount += curr.paidAmount || 0;
            acc.paidTax += curr.paidTax || 0;
            acc.paidTotal += (curr.paidAmount || 0) + (curr.paidTax || 0);
            return acc;
          }, { cost: 0, taxAmount: 0, total: 0, paidAmount: 0, paidTax: 0, paidTotal: 0 });
          totalSum.balance = totalSum.total - totalSum.paidTotal;
          setSum(totalSum);
        })
        .catch(err => {
          console.error('API 호출 실패', err);
          setStatementList([]);
        })
        .finally(() => setIsLoading(false));
    }
  }, [companyId, stDate, edDate]);

  const handleDateChange = (type, value) => {
    if (type === 'start') setStDate(value);
    if (type === 'end') setEdDate(value);
  };

  const reset = () => {
    setStDate(subYears(new Date(), 1));
    setEdDate(new Date());
  };


  return (
    <Grid container direction="column">
      <Grid container item justifyContent="space-between" wrap="nowrap" alignItems="center">
        <Grid container item alignItems="center" xs={6}>
          <ADDate label="Start Date" width="120px" value={stDate}
            onChange={(newValue) => { handleDateChange('start', newValue); }}
          />
          <ADDate label="End Date" width="120px" value={edDate}
            onChange={(newValue) => { handleDateChange('end', newValue); }}
          />
          <IconLoadingButton variant="outlined" loading={isLoading}
            onClick={() => reset()}
            sx={{ position: "relative", top: 5, ml: 1 }}>
              Reset
          </IconLoadingButton>
          <IconLoadingButton variant="contained" loading={isLoading}
            onClick={() => StatementPDF(statementList, sum, companyName, companyId, stDate, edDate)}
            sx={{ position: "relative", top: 5, ml: 1 }}>
              Export PDF
          </IconLoadingButton>
        </Grid>
        <Grid container item alignItems="center" justifyContent="flex-end" xs={4} sx={{ mt: 2 }}>
          <Grid container item direction="column" spacing={1} alignItems="flex-end">
            <Grid item>
              <Box sx={{ fontWeight: 'bold' }}>
                Total: <span style={{ color: '#1976d2' }}>{precisionRound(sum.total).toLocaleString()}</span>
              </Box>
            </Grid>
            <Grid item>
              <Box sx={{ fontWeight: 'bold' }}>
                Paid Total: <span style={{ color: '#388e3c' }}>{precisionRound(sum.paidTotal).toLocaleString()}</span>
              </Box>
            </Grid>
            <Grid item>
              <Box sx={{ fontWeight: 'bold' }}>
                Balance: <span style={{ color: '#d32f2f' }}>{precisionRound(sum.balance).toLocaleString()}</span>
              </Box>
            </Grid>
          </Grid>
        </Grid>

      </Grid>

      <Grid item>
        <Box sx={{ width: '100  %', height: 500 }} className="ag-theme-alpine">
          <AgGridReact
            ref={gridRef}
            rowData={statementList}
            columnDefs={columnDefs}
            defaultColDef={{
              sortable: true,
              resizable: true,
              filter: true,
            }}
            rowSelection="multiple"
          // onSelectionChanged={onSelectionChanged}
          >
          </AgGridReact>
        </Box>
      </Grid>
    </Grid>
  );
};

export default Statement;